import { ActionIcon } from "@mantine/core";
import IconMaximize from "@src/assets/icons/lucide/maximize-2.svg?react";
import IconMessageSquare from "@src/assets/icons/lucide/message-square.svg?react";
import IconTerminal from "@src/assets/icons/lucide/terminal.svg?react";
import IconSidebarCollapse from "@src/assets/icons/tabler/layout-sidebar-left-expand.svg?react";
import IconTrash from "@src/assets/icons/tabler/trash.svg?react";
import IconX from "@src/assets/icons/tabler/x.svg?react";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import clsx from "clsx";
import { useAgentChatContext } from "./hooks/agent-chat-context";
import { useBoardAgentContext } from "./hooks/board-agent-context";
import { useRightDrawerTabStore } from "./right-drawer-tab-store";

type AgentChatHeaderProps = {
	layout?: "drawer" | "fullscreen";
	onClose: () => void;
	onExpand?: () => void;
	onCollapse?: () => void;
};

export function AgentChatHeader({
	layout = "drawer",
	onClose,
	onExpand,
	onCollapse,
}: AgentChatHeaderProps) {
	const { messages, clearChat } = useAgentChatContext();
	const boardAgent = useBoardAgentContext();
	const { activeTab, setActiveTab } = useRightDrawerTabStore();
	const isElectron = Boolean(window.electronAPI?.isElectron);
	const hasMessages = messages.length > 0;

	return (
		<div
			className={clsx(
				"flex shrink-0 items-center justify-between gap-2 border-b border-border-subtle",
				"bg-surface-default px-3 py-1.5 sm:gap-2.5 sm:px-3 sm:py-1.5",
			)}
		>
			<div className="min-w-0 flex items-center gap-2.5">
				{isElectron ? (
					<div
						role="tablist"
						aria-label="Drawer panels"
						className="flex items-center gap-0.5 bg-surface-subtle p-0.5 rounded-lg border border-border-subtle"
					>
						<button
							type="button"
							role="tab"
							aria-selected={activeTab === "agent"}
							aria-controls="agent-chat-drawer"
							id="drawer-tab-agent"
							title="Chat"
							onClick={() => setActiveTab("agent")}
							className={clsx(
								"flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer select-none",
								activeTab === "agent"
									? "bg-surface-default text-text-primary shadow-xs font-semibold"
									: "text-text-tertiary hover:text-text-secondary",
							)}
						>
							<IconMessageSquare className="w-3.5 h-3.5" />
							<span>Chat</span>
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={activeTab === "terminal"}
							aria-controls="agent-terminal-drawer"
							id="drawer-tab-terminal"
							title="Terminal"
							onClick={() => setActiveTab("terminal")}
							className={clsx(
								"flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer select-none",
								activeTab === "terminal"
									? "bg-surface-default text-text-primary shadow-xs font-semibold"
									: "text-text-tertiary hover:text-text-secondary",
							)}
						>
							<IconTerminal className="w-3.5 h-3.5" />
							<span>Terminal</span>
						</button>
					</div>
				) : (
					<div className="min-w-0">
						<p className="truncate text-sm font-semibold text-text-primary">
							Agent chat
						</p>
						<p className="truncate text-xs text-text-tertiary">
							{boardAgent?.snapshot
								? `Board: ${boardAgent.snapshot.whiteboardName}`
								: "Powered by Illumi LLM"}
						</p>
					</div>
				)}

				{isElectron && activeTab === "agent" && (
					<span className="hidden sm:inline-block text-xs text-text-tertiary truncate">
						{boardAgent?.snapshot
							? `Board: ${boardAgent.snapshot.whiteboardName}`
							: "AI Assistant"}
					</span>
				)}
			</div>

			<div className="flex items-center gap-1 shrink-0">
				{activeTab === "agent" && hasMessages ? (
					<Tooltip label="Clear chat">
						<ActionIcon
							variant="subtle"
							color="gray"
							size="lg"
							radius="md"
							title="Clear chat"
							aria-label="Clear chat"
							className="text-text-secondary hover:text-text-primary"
							onClick={clearChat}
						>
							<IconTrash className="h-4 w-4" />
						</ActionIcon>
					</Tooltip>
				) : null}

				{layout === "drawer" && onExpand ? (
					<Tooltip label="Open fullscreen">
						<ActionIcon
							variant="subtle"
							color="gray"
							size="lg"
							radius="md"
							title="Open fullscreen"
							aria-label="Open fullscreen"
							className="text-text-secondary hover:text-text-primary"
							onClick={onExpand}
						>
							<IconMaximize className="h-4 w-4" />
						</ActionIcon>
					</Tooltip>
				) : null}

				{layout === "fullscreen" && onCollapse ? (
					<Tooltip label="Open side panel">
						<ActionIcon
							variant="subtle"
							color="gray"
							size="lg"
							radius="md"
							title="Open side panel"
							aria-label="Open side panel"
							className="text-text-secondary hover:text-text-primary"
							onClick={onCollapse}
						>
							<IconSidebarCollapse className="h-4 w-4" />
						</ActionIcon>
					</Tooltip>
				) : null}

				<ActionIcon
					className="hover:bg-surface-hover-subtle"
					variant="subtle"
					radius="md"
					size="lg"
					color="gray"
					title="Close"
					aria-label="Close"
					onClick={onClose}
				>
					<IconX className="h-4 w-4 sm:h-5 sm:w-5" />
				</ActionIcon>
			</div>
		</div>
	);
}
